<template>
  <div class="login-page">
    <div class="background-overlay"></div>

    <div class="login-container" ref="containerRef">
      <div class="login-card" ref="cardRef">
        <!-- Logo y marca -->
        <div class="brand-header">
          <div class="logo-wrapper">
            <div class="brand-main">MY EVENTO DIGITAL</div>
            <div class="brand-tagline">Invitaciones Digitales Exclusivas</div>
          </div>
        </div>

        <!-- Formulario -->
        <form @submit.prevent="submitForm" class="login-form">
          <div class="form-group">
            <label for="username" class="input-label">Usuario</label>
            <div class="input-container">
              <i class="fas fa-user input-icon"></i>
              <input
                id="username"
                v-model="username"
                type="text"
                class="form-input"
                required
                placeholder="usuario"
                autocomplete="username"
              >
            </div>
          </div>

          <div class="form-group">
            <label for="password" class="input-label">Contraseña</label>
            <div class="input-container">
              <i class="fas fa-lock input-icon"></i>
              <input
                id="password"
                v-model="password"
                :type="showPassword ? 'text' : 'password'"
                class="form-input"
                required
                placeholder="••••••••"
                autocomplete="current-password"
              >
              <button type="button" class="password-toggle" @click="togglePasswordVisibility">
                <i :class="showPassword ? 'fas fa-eye-slash' : 'fas fa-eye'"></i>
              </button>
            </div>
          </div>

          <div class="form-actions">
            <button type="submit" class="login-button">
              <i class="fas fa-sign-in-alt"></i> Iniciar Sesión
            </button>

            <div v-if="errorMessage" class="error-message">
              <i class="fas fa-exclamation-circle"></i> {{ errorMessage }}
            </div>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import axios from 'axios';
import gsap from 'gsap';
import baseUrl from '../api';

const username = ref('');
const password = ref('');
const errorMessage = ref('');
const showPassword = ref(false);

const containerRef = ref(null);
const cardRef = ref(null);
const router = useRouter();

const togglePasswordVisibility = () => {
  showPassword.value = !showPassword.value;
};

const submitForm = async () => {
  if (!username.value || !password.value) {
    errorMessage.value = 'Por favor, completa todos los campos.';
    return;
  }

  try {
    const response = await axios.post(`${baseUrl}/api2/login`, {
      email: username.value,
      password: password.value,
    });

    if (response.data.success) {
      const userId = response.data.user.id_festejada;
      localStorage.setItem('id_festejada', userId);

      const paqueteRes = await axios.get(`${baseUrl}/api2/paquete/${userId}`);
      if (paqueteRes.data?.paquete) {
        localStorage.setItem('paquete', paqueteRes.data.paquete);
      }

      router.push({ name: 'subirlista' });
    } else {
      errorMessage.value = response.data.message || 'Credenciales incorrectas';
    }
  } catch (err) {
    console.error(err);
    errorMessage.value = 'Credenciales incorrectas o error de conexión';
  }
};

onMounted(() => {
  gsap.fromTo(containerRef.value,
    { opacity: 0, y: 30 },
    {
      opacity: 1,
      y: 0,
      duration: 0.8,
      ease: 'power3.out',
      onComplete: () => {
        gsap.fromTo(cardRef.value,
          { opacity: 0, scale: 0.95, y: 10 },
          {
            opacity: 1,
            scale: 1,
            y: 0,
            duration: 0.6,
            ease: 'back.out(1.4)',
            onComplete: () => {
              gsap.to(cardRef.value, {
                boxShadow: '0 15px 40px rgba(0,0,0,0.12)',
                duration: 2,
                repeat: -1,
                yoyo: true,
                ease: 'sine.inOut'
              });
            }
          });
      }
    });
});
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@500;600;700&family=Poppins:wght@400;500;600&display=swap');
@import url('https://cdnjs.cloudflare.com/ajax/libs/font-awesome/5.15.4/css/all.min.css');

.login-page {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #f5f7fa 0%, #c9c7c7 100%);
  position: relative;
  overflow: hidden;
  font-family: 'Poppins', sans-serif;
}

.background-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCA0MCA0MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSIjZWRlZGVkIiBmaWxsLW9wYWNpdHk9IjAuNCI+PHJlY3Qgd2lkdGg9IjQwIiBoZWlnaHQ9IjQwIi8+PC9nPjwvc3ZnPg==');
  opacity: 0.1;
  z-index: 0;
}

.login-container {
  width: 100%;
  max-width: 420px;
  padding: 2rem;
  z-index: 1;
}

.login-card {
  background: white;
  border-radius: 16px;
  padding: 2.5rem;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  border: 1px solid rgba(0, 0, 0, 0.05);
}

.brand-header {
  text-align: center;
  margin-bottom: 2.5rem;
}

.logo-wrapper {
  margin-bottom: 1.5rem;
}

.brand-main {
  font-family: 'Playfair Display', serif;
  font-size: 1.8rem;
  font-weight: 700;
  background: linear-gradient(135deg, #4a5568 0%, #2d3748 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}

.brand-tagline {
  font-size: 0.9rem;
  color: #718096;
  letter-spacing: 0.5px;
}

.login-form {
  margin-top: 1.5rem;
}

.form-group {
  margin-bottom: 1.5rem;
}

.input-label {
  display: block;
  font-size: 0.85rem;
  color: #4a5568;
  margin-bottom: 0.5rem;
  font-weight: 500;
}

.input-container {
  position: relative;
}

.input-icon {
  position: absolute;
  left: 1rem;
  top: 50%;
  transform: translateY(-50%);
  color: #a0aec0;
}

.form-input {
  width: 100%;
  padding: 0.85rem 1rem 0.85rem 2.5rem;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  background-color: #f8fafc;
  font-size: 0.95rem;
  color: #2d3748;
}

.form-input:focus {
  outline: none;
  border-color: #a0aec0;
  background-color: white;
  box-shadow: 0 0 0 3px rgba(160, 174, 192, 0.15);
}

.password-toggle {
  position: absolute;
  right: 1rem;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: #a0aec0;
  cursor: pointer;
  font-size: 1rem;
}

.password-toggle:hover {
  color: #718096;
}

.form-actions {
  margin-top: 2rem;
}

.login-button {
  width: 100%;
  padding: 0.85rem;
  border-radius: 8px;
  font-size: 0.95rem;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  background: linear-gradient(135deg, #151515 0%, #59495d 100%);
  color: white;
  border: none;
  transition: all 0.3s ease;
}

.login-button:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(45, 55, 72, 0.15);
  background: linear-gradient(135deg, #424141 0%, #59495d 100%);
}

.error-message {
  margin-top: 1.5rem;
  padding: 0.75rem;
  background: #fff5f5;
  border: 1px solid #fed7d7;
  border-radius: 8px;
  color: #e53e3e;
  font-size: 0.85rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

@media (max-width: 576px) {
  .login-container {
    padding: 1.5rem;
  }

  .login-card {
    padding: 1.5rem;
  }

  .brand-main {
    font-size: 1.5rem;
  }

  .form-input {
    padding: 0.75rem 1rem 0.75rem 2.25rem;
  }
}
</style>
